/**
 * Legal pages — Privacy & Algemene Voorwaarden / AGB
 *
 * Single source for both languages, structured as sections so the
 * LegalPage component can render consistently.
 *
 * Last updated: 23 mei 2026.
 */

const company = {
  name: 'fliro.tech B.V.',
  trade: 'horrenmaker.nl',
  street: 'Veldoven 6',
  postal: '6826 TT',
  city: 'Arnhem',
  country: { nl: 'Nederland', de: 'Niederlande' },
  email: 'info@horrenmaker.nl',
  phoneNL: '+31 6 51 969 643',
  phoneDE: '+31 6 51 969 643',
  ceo: 'Gerhard Roß',
  kvk: '42004418',
  odr: 'https://ec.europa.eu/consumers/odr',
};

// ------------------------------------------------------------------
// PRIVACY  —  NL
// ------------------------------------------------------------------
const privacyNL = {
  title: 'Privacy\u00ADverklaring',
  intro:
    `Wij respecteren uw privacy. In deze verklaring leest u welke persoonsgegevens ${company.trade} (een handelsnaam van ${company.name}) verwerkt, met welk doel en welke rechten u heeft. Deze verklaring is voor het laatst bijgewerkt op 23 mei 2026.`,
  sections: [
    {
      h: '1. Verantwoordelijke',
      p: [
        `${company.name}, handelend onder de naam ${company.trade}, gevestigd aan ${company.street}, ${company.postal} ${company.city}, ${company.country.nl}, ingeschreven bij de Kamer van Koophandel onder nummer ${company.kvk}.`,
        `Contact: ${company.email} · ${company.phoneNL} · Directeur: ${company.ceo}.`,
      ],
    },
    {
      h: '2. Welke gegevens wij verwerken',
      p: [
        'Wij verwerken alleen gegevens die u zelf aan ons verstrekt, bijvoorbeeld via het offerteformulier, WhatsApp of e-mail:',
      ],
      list: [
        'Naam',
        'E-mailadres',
        'Telefoonnummer (optioneel)',
        'Adresgegevens als u een opmeting of levering wenst',
        'Inhoud van uw bericht (productwens, kozijnmaten, opmerkingen)',
        'Technische gegevens van uw bezoek aan onze website (IP-adres, browsertype, bezoektijd) — uitsluitend voor beveiliging en statistiek',
      ],
    },
    {
      h: '3. Doel van de verwerking',
      p: ['Wij gebruiken uw gegevens uitsluitend om:'],
      list: [
        'Uw aanvraag te beantwoorden en een offerte op te stellen',
        'Een afspraak voor opmeting of plaatsing te plannen',
        'Een overeenkomst tot levering uit te voeren',
        'U te informeren over de status van uw bestelling',
        'Te voldoen aan wettelijke verplichtingen (zoals fiscale bewaarplicht)',
      ],
    },
    {
      h: '4. Rechtsgrondslag',
      p: [
        'Wij verwerken uw gegevens op basis van uw toestemming (artikel 6 lid 1 sub a AVG), de uitvoering van een overeenkomst (sub b), een wettelijke verplichting (sub c) of ons gerechtvaardigd belang om u snel en correct te helpen (sub f).',
      ],
    },
    {
      h: '5. Bewaartermijn',
      p: [
        'Offerteaanvragen die niet tot een opdracht leiden bewaren wij maximaal 12 maanden. Gegevens uit een gesloten overeenkomst bewaren wij conform de wettelijke fiscale bewaarplicht van 7 jaar. Daarna worden gegevens vernietigd of geanonimiseerd.',
      ],
    },
    {
      h: '6. Wie ontvangt uw gegevens',
      p: [
        'Wij delen uw gegevens uitsluitend met partijen die wij nodig hebben om onze dienst te leveren:',
      ],
      list: [
        'Onze hostingpartner voor het functioneren van de website en het opslaan van offerteaanvragen',
        'Een transporteur indien wij producten naar u laten verzenden',
        'De Belastingdienst en andere overheidsinstanties indien wettelijk verplicht',
      ],
      after:
        'Wij verkopen uw gegevens nooit en delen ze nooit voor marketingdoeleinden van derden.',
    },
    {
      h: '7. Cookies',
      p: [
        'Wij plaatsen alleen functionele en analytische cookies die nodig zijn voor de werking van de website. Wij gebruiken geen tracking- of marketingcookies waarvoor toestemming vereist is.',
      ],
    },
    {
      h: '8. Uw rechten',
      p: ['U heeft het recht om:'],
      list: [
        'Inzage te vragen in de gegevens die wij over u verwerken',
        'Onjuiste gegevens te laten corrigeren',
        'Uw gegevens te laten verwijderen ("recht op vergetelheid")',
        'Bezwaar te maken tegen de verwerking',
        'Uw gegevens in een gangbaar formaat over te dragen',
        'Een klacht in te dienen bij de Autoriteit Persoonsgegevens (autoriteitpersoonsgegevens.nl)',
      ],
      after: `Een verzoek stuurt u naar ${company.email}. Wij reageren binnen 30 dagen.`,
    },
    {
      h: '9. Beveiliging',
      p: [
        'Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen tegen verlies of onbevoegde toegang. Alle communicatie met onze website verloopt versleuteld via TLS.',
      ],
    },
    {
      h: '10. Wijzigingen',
      p: [
        'Wij kunnen deze privacyverklaring aanpassen. De meest recente versie vindt u altijd op deze pagina.',
      ],
    },
  ],
};

// ------------------------------------------------------------------
// PRIVACY  —  DE
// ------------------------------------------------------------------
const privacyDE = {
  title: 'Datenschutz\u00ADerklärung',
  intro:
    `Wir nehmen den Schutz Ihrer personenbezogenen Daten ernst. Diese Erklärung informiert Sie darüber, welche Daten ${company.trade} (eine Handelsmarke der ${company.name}) verarbeitet, zu welchem Zweck und welche Rechte Sie haben. Stand: 23. Mai 2026.`,
  sections: [
    {
      h: '1. Verantwortlicher',
      p: [
        `${company.name}, handelnd unter dem Namen ${company.trade}, ${company.street}, ${company.postal} ${company.city}, ${company.country.de}, eingetragen bei der Kamer van Koophandel unter ${company.kvk}.`,
        `Kontakt: ${company.email} · ${company.phoneDE} · Geschäftsführer: ${company.ceo}.`,
      ],
    },
    {
      h: '2. Welche Daten wir verarbeiten',
      p: [
        'Wir verarbeiten ausschließlich Daten, die Sie uns selbst übermitteln — beispielsweise über das Angebotsformular, WhatsApp oder per E-Mail:',
      ],
      list: [
        'Name',
        'E-Mail-Adresse',
        'Telefonnummer (optional)',
        'Adressdaten, sofern Sie ein Aufmaß oder eine Lieferung wünschen',
        'Inhalt Ihrer Nachricht (Produktwunsch, Rahmenmaße, Anmerkungen)',
        'Technische Daten Ihres Besuchs (IP-Adresse, Browsertyp, Besuchszeit) — ausschließlich für Sicherheit und Statistik',
      ],
    },
    {
      h: '3. Zweck der Verarbeitung',
      p: ['Ihre Daten verwenden wir ausschließlich für:'],
      list: [
        'Die Beantwortung Ihrer Anfrage und Erstellung eines Angebots',
        'Die Planung eines Aufmaß- oder Montagetermins',
        'Die Erfüllung eines Liefervertrags',
        'Die Information über den Status Ihrer Bestellung',
        'Die Erfüllung gesetzlicher Pflichten (z.B. steuerrechtliche Aufbewahrung)',
      ],
    },
    {
      h: '4. Rechtsgrundlage',
      p: [
        'Wir verarbeiten Ihre Daten auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO), zur Vertragserfüllung (lit. b), zur Erfüllung gesetzlicher Pflichten (lit. c) oder aufgrund unseres berechtigten Interesses, Sie schnell und korrekt zu beraten (lit. f).',
      ],
    },
    {
      h: '5. Speicherdauer',
      p: [
        'Angebotsanfragen, die nicht zu einem Auftrag führen, speichern wir maximal 12 Monate. Daten aus einem Vertragsverhältnis bewahren wir entsprechend der gesetzlichen Aufbewahrungspflicht von 7 Jahren. Danach werden Daten gelöscht oder anonymisiert.',
      ],
    },
    {
      h: '6. Empfänger Ihrer Daten',
      p: [
        'Wir geben Ihre Daten ausschließlich an Partner weiter, die wir für unsere Leistung benötigen:',
      ],
      list: [
        'Unser Hosting-Partner für den Betrieb der Website und die Speicherung von Anfragen',
        'Ein Transportdienstleister bei Versand der Ware',
        'Die niederländische Steuerbehörde sowie weitere Behörden, sofern gesetzlich vorgeschrieben',
      ],
      after:
        'Wir verkaufen Ihre Daten niemals und geben sie niemals zu Marketingzwecken Dritter weiter.',
    },
    {
      h: '7. Cookies',
      p: [
        'Wir setzen ausschließlich technisch notwendige und anonyme statistische Cookies ein. Es werden keine Tracking- oder Marketing-Cookies verwendet, die Ihre Einwilligung erfordern.',
      ],
    },
    {
      h: '8. Ihre Rechte',
      p: ['Sie haben das Recht:'],
      list: [
        'Auskunft über die von uns verarbeiteten Daten zu erhalten',
        'Unrichtige Daten berichtigen zu lassen',
        'Ihre Daten löschen zu lassen ("Recht auf Vergessenwerden")',
        'Der Verarbeitung zu widersprechen',
        'Ihre Daten in einem gängigen Format übertragen zu lassen',
        'Sich bei der zuständigen Aufsichtsbehörde zu beschweren — in den Niederlanden: Autoriteit Persoonsgegevens (autoriteitpersoonsgegevens.nl)',
      ],
      after: `Bitte richten Sie Anfragen an ${company.email}. Wir antworten innerhalb von 30 Tagen.`,
    },
    {
      h: '9. Sicherheit',
      p: [
        'Wir treffen geeignete technische und organisatorische Maßnahmen, um Ihre Daten vor Verlust oder unbefugtem Zugriff zu schützen. Sämtliche Kommunikation mit unserer Website erfolgt verschlüsselt über TLS.',
      ],
    },
    {
      h: '10. Änderungen',
      p: [
        'Wir können diese Datenschutzerklärung anpassen. Die jeweils aktuelle Version finden Sie stets auf dieser Seite.',
      ],
    },
  ],
};

// ------------------------------------------------------------------
// VOORWAARDEN / AGB  —  NL
// ------------------------------------------------------------------
const termsNL = {
  title: 'Algemene Voorwaarden',
  intro:
    `Deze voorwaarden gelden voor alle offertes, overeenkomsten en leveringen van ${company.trade} (handelsnaam van ${company.name}). Versie: 23 mei 2026.`,
  sections: [
    {
      h: '1. Toepasselijkheid',
      p: [
        `Deze algemene voorwaarden zijn van toepassing op iedere aanbieding, offerte en overeenkomst tussen ${company.name} en u als opdrachtgever, voor zover van deze voorwaarden niet schriftelijk is afgeweken.`,
      ],
    },
    {
      h: '2. Aanbiedingen en offertes',
      p: [
        'Alle offertes zijn vrijblijvend en geldig gedurende 30 dagen, tenzij anders vermeld. Een overeenkomst komt tot stand zodra u de offerte schriftelijk of per e-mail accepteert.',
        'Wij behouden ons het recht voor om kennelijke vergissingen in de offerte te corrigeren.',
      ],
    },
    {
      h: '3. Prijzen en betaling',
      p: [
        'Alle prijzen zijn in euro en inclusief 21% btw, tenzij anders vermeld. Verzendkosten binnen Nederland zijn gratis voor de aangegeven productgroepen.',
        'Betaling vindt plaats binnen 14 dagen na factuurdatum, tenzij anders overeengekomen. Bij vertraging zijn wij gerechtigd wettelijke handelsrente in rekening te brengen.',
      ],
    },
    {
      h: '4. Levering en montage',
      p: [
        'De levertijd wordt in de offerte aangegeven en is indicatief. Wij streven naar levering binnen 2 tot 4 weken na opdrachtbevestiging.',
        'Indien montage door ons wordt verzorgd, plannen wij in overleg een afspraak. U zorgt ervoor dat onze monteur op de afgesproken tijd toegang heeft tot de plaatsingslocatie.',
      ],
    },
    {
      h: '5. Maatwerk en herroepingsrecht',
      p: [
        'Onze producten worden op maat gemaakt op basis van de door u aangeleverde of door ons opgenomen kozijnmaten. Conform artikel 6:230p van het Burgerlijk Wetboek is het wettelijk herroepingsrecht uitgesloten voor producten die op specificatie van de consument zijn vervaardigd.',
        'Voor standaardproducten zonder maatwerk geldt het herroepingsrecht van 14 dagen na ontvangst.',
      ],
    },
    {
      h: '6. Garantie',
      p: [
        'Wij geven 5 jaar garantie op constructie en afwerking van onze horren bij normaal huishoudelijk gebruik. Slijtage door onjuist gebruik, schade door geweld of onjuist onderhoud valt buiten de garantie.',
        'Een garantieclaim meldt u binnen redelijke termijn na ontdekking via e-mail. Wij beoordelen de claim, en repareren of vervangen het product naar onze keuze.',
      ],
    },
    {
      h: '7. Aansprakelijkheid',
      p: [
        'Onze aansprakelijkheid is beperkt tot het factuurbedrag van de betreffende opdracht. Wij zijn niet aansprakelijk voor gevolgschade, indirecte schade of schade veroorzaakt door onjuiste opgave van maten door de opdrachtgever.',
      ],
    },
    {
      h: '8. Overmacht',
      p: [
        'In geval van overmacht (zoals stakingen, transportstoringen of overheidsmaatregelen) zijn wij gerechtigd de levertijd te verlengen of de overeenkomst te ontbinden zonder schadevergoedingsplicht.',
      ],
    },
    {
      h: '9. Klachten en geschillen',
      p: [
        `Klachten kunt u sturen naar ${company.email}. Wij reageren binnen 5 werkdagen.`,
        `Op alle overeenkomsten is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter te Arnhem. Consumenten kunnen tevens gebruikmaken van het Europese ODR-platform: ${company.odr}.`,
      ],
    },
  ],
};

// ------------------------------------------------------------------
// VOORWAARDEN / AGB  —  DE
// ------------------------------------------------------------------
const termsDE = {
  title: 'Allgemeine Geschäfts\u00ADbedingungen',
  intro:
    `Diese AGB gelten für alle Angebote, Verträge und Lieferungen der ${company.trade} (Handelsname der ${company.name}). Stand: 23. Mai 2026.`,
  sections: [
    {
      h: '1. Geltungsbereich',
      p: [
        `Diese Allgemeinen Geschäftsbedingungen gelten für sämtliche Angebote, Aufträge und Lieferungen zwischen der ${company.name} und Ihnen als Auftraggeber, soweit nicht schriftlich abweichende Vereinbarungen getroffen wurden.`,
      ],
    },
    {
      h: '2. Angebote und Vertragsschluss',
      p: [
        'Alle Angebote sind freibleibend und 30 Tage gültig, sofern nicht anders angegeben. Ein Vertrag kommt zustande, sobald Sie das Angebot schriftlich oder per E-Mail bestätigen.',
        'Offensichtliche Irrtümer oder Druckfehler binden uns nicht.',
      ],
    },
    {
      h: '3. Preise und Zahlung',
      p: [
        'Alle Preise verstehen sich in Euro inklusive 21% niederländischer Mehrwertsteuer, sofern nicht anders ausgewiesen. Versand innerhalb der Niederlande ist für die ausgewiesenen Produktgruppen kostenlos.',
        'Die Zahlung erfolgt innerhalb von 14 Tagen nach Rechnungsdatum, sofern nicht anders vereinbart. Bei Zahlungsverzug sind wir berechtigt, Verzugszinsen in gesetzlicher Höhe zu berechnen.',
      ],
    },
    {
      h: '4. Lieferung und Montage',
      p: [
        'Die Lieferzeit ist im Angebot angegeben und unverbindlich. Wir bemühen uns um Lieferung innerhalb von 2 bis 4 Wochen nach Auftragsbestätigung.',
        'Übernimmt der Auftragnehmer die Montage, wird der Termin in Absprache vereinbart. Sie sorgen dafür, dass unser Monteur zum vereinbarten Zeitpunkt Zugang zum Einbauort hat.',
      ],
    },
    {
      h: '5. Maßanfertigung und Widerrufsrecht',
      p: [
        'Unsere Produkte werden nach den von Ihnen mitgeteilten oder durch uns aufgenommenen Rahmenmaßen gefertigt. Gemäß § 312g Abs. 2 Nr. 1 BGB bzw. Art. 6:230p des niederländischen Burgerlijk Wetboek ist das gesetzliche Widerrufsrecht für Maßanfertigungen ausgeschlossen.',
        'Für Standardartikel ohne Maßanfertigung gilt das gesetzliche Widerrufsrecht von 14 Tagen nach Erhalt der Ware.',
      ],
    },
    {
      h: '6. Gewährleistung',
      p: [
        'Wir gewähren 5 Jahre Garantie auf Konstruktion und Verarbeitung unserer Insektenschutzprodukte bei normaler häuslicher Nutzung. Verschleiß durch unsachgemäßen Gebrauch, Beschädigung durch Gewalteinwirkung oder fehlerhafte Pflege sind von der Garantie ausgeschlossen.',
        'Eine Mängelrüge melden Sie bitte innerhalb angemessener Frist nach Entdeckung per E-Mail. Wir prüfen die Reklamation und reparieren oder ersetzen den Artikel nach unserer Wahl.',
      ],
    },
    {
      h: '7. Haftung',
      p: [
        'Unsere Haftung ist auf den Rechnungsbetrag des betreffenden Auftrags begrenzt. Wir haften nicht für Folgeschäden, indirekte Schäden oder Schäden, die durch fehlerhafte Maßangaben des Auftraggebers verursacht wurden.',
      ],
    },
    {
      h: '8. Höhere Gewalt',
      p: [
        'Bei höherer Gewalt (z.B. Streiks, Transportstörungen, behördliche Maßnahmen) sind wir berechtigt, die Lieferzeit zu verlängern oder vom Vertrag zurückzutreten, ohne dass daraus Schadenersatzansprüche entstehen.',
      ],
    },
    {
      h: '9. Beschwerden und Streitbeilegung',
      p: [
        `Beschwerden richten Sie bitte an ${company.email}. Wir antworten innerhalb von 5 Werktagen.`,
        `Auf alle Verträge findet niederländisches Recht Anwendung. Gerichtsstand ist Arnhem. Verbraucher können zusätzlich die EU-Online-Streitbeilegungsplattform nutzen: ${company.odr}.`,
      ],
    },
  ],
};

export const legalContent = {
  privacy: { nl: privacyNL, de: privacyDE },
  terms:   { nl: termsNL,   de: termsDE   },
  impressum: { nl: {
    title: 'Colofon',
    intro: `Wettelijke informatie van ${company.trade}, een handelsnaam van ${company.name}.`,
    sections: [
      { h: 'Bedrijfsgegevens', p: [
        `${company.name}`,
        `Handelend onder: ${company.trade}`,
        `${company.street}, ${company.postal} ${company.city}, ${company.country.nl}`,
      ]},
      { h: 'Contact', p: [
        `E-mail: ${company.email}`,
        `Telefoon: ${company.phoneNL}`,
      ]},
      { h: 'Wettelijke vertegenwoordiging', p: [`Directeur: ${company.ceo}`]},
      { h: 'Inschrijving', p: [`Kamer van Koophandel: ${company.kvk}`]},
      { h: 'Geschillenbeslechting', p: [
        'De Europese Commissie biedt een platform voor online geschillenbeslechting (ODR), bereikbaar via:',
        company.odr,
        'Wij zijn niet verplicht en niet bereid deel te nemen aan een geschillenbeslechtingsprocedure voor een consumentenarbitragecommissie.',
      ]},
      { h: 'Aansprakelijkheid voor inhoud', p: [
        'Als dienstaanbieder zijn wij verantwoordelijk voor eigen inhoud op deze pagina\'s volgens de algemene wetgeving. Wij zijn echter niet verplicht doorgegeven of opgeslagen vreemde informatie te bewaken of op omstandigheden te onderzoeken die op een onrechtmatige activiteit wijzen.',
      ]},
    ],
  }, de: {
    title: 'Impressum',
    intro: `Angaben gemäß § 5 TMG für ${company.trade}, eine Handelsmarke der ${company.name}.`,
    sections: [
      { h: 'Anbieter', p: [
        `${company.name}`,
        `handelnd unter: ${company.trade}`,
        `${company.street}, ${company.postal} ${company.city}, ${company.country.de}`,
      ]},
      { h: 'Kontakt', p: [
        `E-Mail: ${company.email}`,
        `Telefon: ${company.phoneDE}`,
      ]},
      { h: 'Vertretungsberechtigt', p: [`Geschäftsführer: ${company.ceo}`]},
      { h: 'Registereintrag', p: [`Kamer van Koophandel: ${company.kvk}`]},
      { h: 'Streitschlichtung', p: [
        'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit, erreichbar unter:',
        company.odr,
        'Wir sind nicht bereit oder verpflichtet, an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.',
      ]},
      { h: 'Haftung für Inhalte', p: [
        'Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen.',
      ]},
    ],
  }},
};
